import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Icon from '@/components/AppIcon';
import useTranslation from '@/hooks/useTranslation';
import formatTranslation from '@/utils/formatTranslation';

const SEVERITY_OPTION_KEYS = [
  { value: 'high', labelKey: 'incidentReports.modal.fields.severity.options.high' },
  { value: 'medium', labelKey: 'incidentReports.modal.fields.severity.options.medium' },
  { value: 'low', labelKey: 'incidentReports.modal.fields.severity.options.low' },
];

const STATUS_OPTION_KEYS = [
  { value: 'open', labelKey: 'incidentReports.modal.fields.status.options.open' },
  { value: 'investigating', labelKey: 'incidentReports.modal.fields.status.options.investigating' },
  { value: 'resolved', labelKey: 'incidentReports.modal.fields.status.options.resolved' },
];

const convertIsoToLocalInput = (isoString) => {
  if (!isoString) {
    return '';
  }
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 16);
};

const getDefaultReporterName = (currentUser) => {
  if (!currentUser) {
    return '';
  }

  return (
    currentUser?.fullName
    ?? currentUser?.name
    ?? [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(' ')
    ?? currentUser?.email
    ?? ''
  );
};

const normalizePhotos = (photos, getAttachmentLabel = (index) => `Photo ${index}`) => {
  if (!Array.isArray(photos)) {
    return [];
  }

  return photos
    .filter(Boolean)
    .map((photo, index) => {
      if (typeof photo === 'string') {
        return {
          id: `existing-${index}`,
          fileName: photo.split('/').pop() || getAttachmentLabel(index + 1),
          previewUrl: photo,
          persisted: true,
        };
      }

      return {
        id: photo?.id ?? `photo-${index}`,
        fileName: photo?.fileName ?? getAttachmentLabel(index + 1),
        previewUrl: photo?.previewUrl ?? '',
        persisted: !!photo?.persisted,
        shouldRevoke: !!photo?.shouldRevoke,
      };
    });
};

const TextAreaField = ({ label, value, onChange, required = false, disabled = false, description, error, placeholder }) => (
  <div className="space-y-2">
    {label && (
      <label className={`text-sm font-medium leading-none ${error ? 'text-destructive' : 'text-foreground'}`}>
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </label>
    )}
    <textarea
      className={`min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${error ? 'border-destructive focus-visible:ring-destructive' : ''}`}
      value={value}
      onChange={onChange}
      required={required}
      disabled={disabled}
      placeholder={placeholder}
    />
    {description && !error ? <p className="text-sm text-muted-foreground">{description}</p> : null}
    {error ? <p className="text-sm text-destructive">{error}</p> : null}
  </div>
);

const IncidentFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialIncident = null,
  mode = 'create',
  locations = [],
  currentUser = null,
}) => {
  const { t } = useTranslation(['incidentReports']);
  const defaultReporter = useMemo(() => getDefaultReporterName(currentUser), [currentUser]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    severity: 'medium',
    status: 'open',
    reportedAt: convertIsoToLocalInput(new Date().toISOString()),
    reportedBy: defaultReporter,
    involvedEmployees: [],
    actionsTaken: '',
    followUp: '',
    impact: '',
  });
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [errors, setErrors] = useState({});
  const [newPerson, setNewPerson] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const locationOptions = useMemo(() => {
    if (!Array.isArray(locations)) {
      return [];
    }

    return locations
      .filter(Boolean)
      .map((location) => ({
        value: location?.name ?? location?.id ?? location,
        label: location?.name ?? location?.label ?? String(location ?? ''),
      }));
  }, [locations]);

  const severityOptions = useMemo(
    () =>
      SEVERITY_OPTION_KEYS.map((option) => ({
        ...option,
        label: t(option.labelKey),
      })),
    [t],
  );

  const statusOptions = useMemo(
    () =>
      STATUS_OPTION_KEYS.map((option) => ({
        ...option,
        label: t(option.labelKey),
      })),
    [t],
  );

  const formatAttachmentName = useCallback(
    (index) => formatTranslation(t('incidentReports.defaults.attachmentName'), { index }),
    [t],
  );

  const isReadOnly = mode === 'view';

  useEffect(() => {
    if (!isOpen) {
      // Clean up any object URLs when closing the modal
      setPhotoPreviews((existing) => {
        existing.forEach((photo) => {
          if (photo?.shouldRevoke && photo?.previewUrl) {
            URL.revokeObjectURL(photo.previewUrl);
          }
        });
        return [];
      });
      setNewPerson('');
      setErrors({});
      setIsSubmitting(false);
      return;
    }

    const description = initialIncident?.description ?? initialIncident?.summary ?? '';

    setFormData({
      title: initialIncident?.title ?? '',
      description,
      location: initialIncident?.location ?? (locationOptions?.[0]?.value ?? ''),
      severity: initialIncident?.severity ?? 'medium',
      status: initialIncident?.status ?? 'open',
      reportedAt: convertIsoToLocalInput(initialIncident?.reportedAt ?? new Date().toISOString()),
      reportedBy: initialIncident?.reportedBy ?? defaultReporter,
      involvedEmployees: Array.isArray(initialIncident?.involvedEmployees)
        ? initialIncident.involvedEmployees.filter(Boolean)
        : [],
      actionsTaken: initialIncident?.actionsTaken ?? '',
      followUp: initialIncident?.followUp ?? '',
      impact: initialIncident?.impact ?? '',
    });

    setPhotoPreviews(normalizePhotos(initialIncident?.photos, formatAttachmentName));
    setErrors({});
    setNewPerson('');
  }, [defaultReporter, formatAttachmentName, initialIncident, isOpen, locationOptions]);

  useEffect(() => () => {
    // Revoke any object URLs when unmounting the modal
    photoPreviews.forEach((photo) => {
      if (photo?.shouldRevoke && photo?.previewUrl) {
        URL.revokeObjectURL(photo.previewUrl);
      }
    });
  }, [photoPreviews]);

  const handleAddPerson = () => {
    const trimmed = newPerson.trim();
    if (!trimmed) {
      return;
    }
    setFormData((prev) => ({
      ...prev,
      involvedEmployees: prev.involvedEmployees.includes(trimmed)
        ? prev.involvedEmployees
        : [...prev.involvedEmployees, trimmed],
    }));
    setNewPerson('');
  };

  const handleRemovePerson = (person) => {
    setFormData((prev) => ({
      ...prev,
      involvedEmployees: prev.involvedEmployees.filter((entry) => entry !== person),
    }));
  };

  const handlePhotoUpload = (event) => {
    const files = Array.from(event?.target?.files ?? []);
    if (files.length === 0) {
      return;
    }

    const uploads = files.map((file) => ({
      id: `upload-${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      fileName: file.name,
      previewUrl: URL.createObjectURL(file),
      shouldRevoke: true,
    }));

    setPhotoPreviews((prev) => [...prev, ...uploads]);
    // reset input value so same file can be uploaded again if needed
    if (event?.target) {
      event.target.value = '';
    }
  };

  const handleRemovePhoto = (photoId) => {
    setPhotoPreviews((prev) => {
      const target = prev.find((item) => item.id === photoId);
      if (target?.shouldRevoke && target?.previewUrl) {
        URL.revokeObjectURL(target.previewUrl);
      }
      return prev.filter((item) => item.id !== photoId);
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isReadOnly) {
      onClose?.();
      return;
    }

    const validationErrors = {};

    if (!formData.title.trim()) {
      validationErrors.title = t('incidentReports.validation.title');
    }

    if (!formData.description.trim()) {
      validationErrors.description = t('incidentReports.validation.description');
    }

    if (!formData.location) {
      validationErrors.location = t('incidentReports.validation.location');
    }

    if (!formData.reportedAt) {
      validationErrors.reportedAt = t('incidentReports.validation.reportedAt');
    }

    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    const payload = {
      ...formData,
      reportedAt: formData.reportedAt
        ? new Date(formData.reportedAt).toISOString()
        : new Date().toISOString(),
      photos: photoPreviews.map((photo, index) => ({
        id: photo.id ?? `photo-${index}`,
        fileName: photo.fileName ?? formatAttachmentName(index + 1),
        previewUrl: photo.previewUrl ?? '',
        persisted: photo.persisted ?? false,
      })),
      summary: formData.description,
    };

    try {
      await Promise.resolve(onSubmit?.(payload));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-elevation-2 w-full max-w-3xl max-h-[95vh] overflow-y-auto">
        <div className="flex items-start justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              {mode === 'create' && t('incidentReports.modal.titles.create')}
              {mode === 'edit' && t('incidentReports.modal.titles.edit')}
              {mode === 'view' && t('incidentReports.modal.titles.view')}
            </h2>
            <p className="text-sm text-muted-foreground mt-2">{t('incidentReports.modal.description')}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label={t('incidentReports.modal.fields.title.label')}
              placeholder={t('incidentReports.modal.fields.title.placeholder')}
              value={formData.title}
              onChange={(event) =>
                setFormData((prev) => ({ ...prev, title: event?.target?.value ?? '' }))
              }
              error={errors.title}
              required
              disabled={isReadOnly}
            />

            <Input
              label={t('incidentReports.modal.fields.reportedBy.label')}
              placeholder={t('incidentReports.modal.fields.reportedBy.placeholder')}
              value={formData.reportedBy}
              onChange={(event) =>
                setFormData((prev) => ({ ...prev, reportedBy: event?.target?.value ?? '' }))
              }
              disabled={isReadOnly}
            />

            {locationOptions.length > 0 ? (
              <Select
                label={t('incidentReports.modal.fields.location.label')}
                placeholder={t('incidentReports.modal.fields.location.placeholder')}
                options={locationOptions}
                value={formData.location}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, location: value ?? '' }))
                }
                required
                error={errors.location}
                disabled={isReadOnly}
                searchable
              />
            ) : (
              <Input
                label={t('incidentReports.modal.fields.location.label')}
                placeholder={t('incidentReports.modal.fields.location.fallbackPlaceholder')}
                value={formData.location}
                onChange={(event) =>
                  setFormData((prev) => ({ ...prev, location: event?.target?.value ?? '' }))
                }
                required
                error={errors.location}
                disabled={isReadOnly}
              />
            )}

            <Input
              type="datetime-local"
              label={t('incidentReports.modal.fields.reportedAt.label')}
              value={formData.reportedAt}
              onChange={(event) =>
                setFormData((prev) => ({ ...prev, reportedAt: event?.target?.value ?? '' }))
              }
              required
              error={errors.reportedAt}
              disabled={isReadOnly}
            />

            <Select
              label={t('incidentReports.modal.fields.severity.label')}
              options={severityOptions}
              value={formData.severity}
              onChange={(value) => setFormData((prev) => ({ ...prev, severity: value }))}
              disabled={isReadOnly}
            />

            <Select
              label={t('incidentReports.modal.fields.status.label')}
              options={statusOptions}
              value={formData.status}
              onChange={(value) => setFormData((prev) => ({ ...prev, status: value }))}
              disabled={isReadOnly}
            />
          </div>

          <TextAreaField
            label={t('incidentReports.modal.fields.description.label')}
            placeholder={t('incidentReports.modal.fields.description.placeholder')}
            value={formData.description}
            onChange={(event) =>
              setFormData((prev) => ({ ...prev, description: event?.target?.value ?? '' }))
            }
            required
            disabled={isReadOnly}
            error={errors.description}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <TextAreaField
              label={t('incidentReports.modal.fields.impact.label')}
              placeholder={t('incidentReports.modal.fields.impact.placeholder')}
              value={formData.impact}
              onChange={(event) =>
                setFormData((prev) => ({ ...prev, impact: event?.target?.value ?? '' }))
              }
              disabled={isReadOnly}
            />

            <TextAreaField
              label={t('incidentReports.modal.fields.actionsTaken.label')}
              placeholder={t('incidentReports.modal.fields.actionsTaken.placeholder')}
              value={formData.actionsTaken}
              onChange={(event) =>
                setFormData((prev) => ({ ...prev, actionsTaken: event?.target?.value ?? '' }))
              }
              disabled={isReadOnly}
            />
          </div>

          <TextAreaField
            label={t('incidentReports.modal.fields.followUp.label')}
            placeholder={t('incidentReports.modal.fields.followUp.placeholder')}
            value={formData.followUp}
            onChange={(event) =>
              setFormData((prev) => ({ ...prev, followUp: event?.target?.value ?? '' }))
            }
            disabled={isReadOnly}
          />

          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">{t('incidentReports.modal.people.label')}</label>
            <p className="text-sm text-muted-foreground">
              {t('incidentReports.modal.people.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Input
                value={newPerson}
                onChange={(event) => setNewPerson(event?.target?.value ?? '')}
                placeholder={t('incidentReports.modal.people.placeholder')}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleAddPerson();
                  }
                }}
                disabled={isReadOnly}
              />
              <Button
                type="button"
                variant="secondary"
                onClick={handleAddPerson}
                disabled={isReadOnly || !newPerson.trim()}
                iconName="UserPlus"
              >
                {t('incidentReports.modal.people.addButton')}
              </Button>
            </div>
            {formData.involvedEmployees.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {formData.involvedEmployees.map((person) => (
                  <span
                    key={person}
                    className="inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1 text-sm text-muted-foreground"
                  >
                    <Icon name="User" size={14} />
                    <span>{person}</span>
                    {!isReadOnly && (
                      <button
                        type="button"
                        className="text-muted-foreground/70 hover:text-destructive"
                        onClick={() => handleRemovePerson(person)}
                        aria-label={formatTranslation(t('incidentReports.modal.people.removeAria'), {
                          name: person,
                        })}
                      >
                        <Icon name="X" size={14} />
                      </button>
                    )}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t('incidentReports.modal.people.empty')}</p>
            )}
          </div>

          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">{t('incidentReports.modal.photos.label')}</label>
            <p className="text-sm text-muted-foreground">
              {t('incidentReports.modal.photos.description')}
            </p>
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center ${
                isReadOnly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-primary'
              }`}
            >
              <label className="flex flex-col items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                <Icon name="ImagePlus" size={24} className="text-muted-foreground/70" />
                <span>{t('incidentReports.modal.photos.cta')}</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  className="hidden"
                  disabled={isReadOnly}
                />
              </label>
            </div>

            {photoPreviews.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {photoPreviews.map((photo) => (
                  <div
                    key={photo.id}
                    className="border border-border rounded-lg overflow-hidden bg-muted/40 flex"
                  >
                    {photo.previewUrl ? (
                      <div className="w-24 h-24 bg-black/5 flex-shrink-0">
                        <img
                          src={photo.previewUrl}
                          alt={photo.fileName ?? t('incidentReports.modal.photos.attachmentAlt')}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-24 h-24 bg-muted flex items-center justify-center flex-shrink-0">
                        <Icon name="Image" size={24} className="text-muted-foreground/70" />
                      </div>
                    )}
                    <div className="flex-1 p-3 flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-foreground truncate">{photo.fileName}</p>
                        <p className="text-xs text-muted-foreground">
                          {photo.persisted
                            ? t('incidentReports.modal.photos.existing')
                            : t('incidentReports.modal.photos.new')}
                        </p>
                      </div>
                      {!isReadOnly && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemovePhoto(photo.id)}
                          aria-label={formatTranslation(t('incidentReports.modal.photos.removeAria'), {
                            fileName: photo.fileName,
                          })}
                        >
                          <Icon name="Trash2" size={16} />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('incidentReports.modal.actions.close')}
            </Button>
            {!isReadOnly && (
              <Button type="submit" variant="default" loading={isSubmitting} iconName="Save">
                {t('incidentReports.modal.actions.save')}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default IncidentFormModal;
